import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import {
  getGoldSetQueueUrl,
  getGoldSetReviewUrl,
  getNextTodo,
  getPreviousItem,
  type GoldSetLabelInput,
  useGoldSetCategories,
  useGoldSetDocument,
  useGoldSetPass,
  useGoldSetQueue,
  useSaveGoldSetLabel,
} from '~/entities/gold-set'
import { getApiErrorStatus } from '~/shared/lib/api-error'
import { Alert, Button, ScrollArea, SkeletonBlock, toast } from '~/shared/ui'
import { AllDone } from './all-done'
import { ReviewForm } from './review-form'
import { ReviewHeader } from './review-header'
import { ReviewPdf } from './review-pdf'
import { ReviewRulesPanel } from './review-rules-panel'

function getSaveErrorMessage(error: unknown): string {
  const status = getApiErrorStatus(error)
  if (status === 400) return 'The server rejected this label: the category is not in the list.'
  if (status === 404) return 'This document is no longer in the gold set for this pass.'
  if (status === 403) return 'The gold set review is not available for your account.'
  return 'Could not save the label. Please try again.'
}

function FormSkeleton() {
  return (
    <div className="flex flex-col gap-5">
      <SkeletonBlock className="h-16 w-full" />
      <SkeletonBlock className="h-8 w-full" />
      <SkeletonBlock className="h-8 w-full" />
      <SkeletonBlock className="h-28 w-full" />
    </div>
  )
}

export function GoldSetReviewPage() {
  const documentKey = useParams<'documentKey'>().documentKey as string
  const [pass] = useGoldSetPass()
  const navigate = useNavigate()
  const queueQuery = useGoldSetQueue(pass)
  const documentQuery = useGoldSetDocument(documentKey, pass)
  const categoriesQuery = useGoldSetCategories()
  const saveLabel = useSaveGoldSetLabel()
  const [allDoneAt, setAllDoneAt] = useState<string | null>(null)
  const currentKeyRef = useRef(documentKey)

  useEffect(() => {
    currentKeyRef.current = documentKey
  }, [documentKey])

  const items = queueQuery.data?.items
  const nextTodo = items ? getNextTodo(items, documentKey) : undefined
  const previousItem = items ? getPreviousItem(items, documentKey) : undefined
  const isSavingThis = saveLabel.isPending && saveLabel.variables?.documentKey === documentKey
  const saveError =
    saveLabel.isError && saveLabel.variables?.documentKey === documentKey
      ? getSaveErrorMessage(saveLabel.error)
      : null

  const goToNextTodo = () => {
    if (!items) {
      navigate(getGoldSetQueueUrl(pass))
    } else if (nextTodo) {
      navigate(getGoldSetReviewUrl(nextTodo.short_key, pass))
    } else {
      setAllDoneAt(documentKey)
    }
  }

  const handleSave = (label: GoldSetLabelInput) => {
    saveLabel.mutate(
      { documentKey, label },
      {
        onSuccess: () => {
          toast('Label saved')
          // The reviewer may have moved on (browser back) while the save was in flight.
          if (currentKeyRef.current === documentKey) goToNextTodo()
        },
      },
    )
  }

  const documentErrorStatus = getApiErrorStatus(documentQuery.error)

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ReviewHeader
        pass={pass}
        documentKey={documentKey}
        document={documentQuery.data}
        queue={queueQuery.data}
        isQueueError={queueQuery.isError}
      />
      {allDoneAt === documentKey ? (
        <AllDone
          pass={pass}
          onShowLastDocument={() => setAllDoneAt(null)}
        />
      ) : documentQuery.isError ? (
        <div className="flex flex-col items-start gap-3 p-6">
          <Alert
            className="grow-0"
            appearance="ERROR"
            materialIcon="error"
          >
            {documentErrorStatus === 404
              ? `This document is not in your gold-set queue for pass ${pass}.`
              : documentErrorStatus === 403
                ? 'The gold set review is not available for your account.'
                : 'Could not load this document.'}
          </Alert>
          <div className="flex gap-2">
            {documentErrorStatus !== 404 && documentErrorStatus !== 403 && (
              <Button onClick={() => documentQuery.refetch()}>Try again</Button>
            )}
            <Button to={getGoldSetQueueUrl(pass)}>Back to the queue</Button>
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1">
          <div className="border-divider h-full w-[60%] shrink-0 border-r">
            <ReviewPdf
              key={documentKey}
              documentKey={documentKey}
            />
          </div>
          <ScrollArea className="h-full min-w-0 flex-1">
            <div className="flex flex-col gap-6 p-6">
              {documentQuery.data?.status === 'done_by_other' && !documentQuery.data.my_label && (
                <Alert
                  className="grow-0"
                  appearance="INFO"
                  materialIcon="info"
                >
                  Someone else has already labelled this document in this pass.
                </Alert>
              )}
              {documentQuery.isPending || categoriesQuery.isPending ? (
                <FormSkeleton />
              ) : categoriesQuery.isError ? (
                <div className="flex flex-col items-start gap-3">
                  <Alert
                    className="grow-0"
                    appearance="ERROR"
                    materialIcon="error"
                  >
                    Could not load the categories.
                  </Alert>
                  <Button onClick={() => categoriesQuery.refetch()}>Try again</Button>
                </div>
              ) : (
                <ReviewForm
                  key={documentKey}
                  phases={categoriesQuery.data.phases}
                  pass={pass}
                  initialLabel={documentQuery.data.my_label}
                  isSaving={isSavingThis}
                  saveError={saveError}
                  canSkip={!!nextTodo}
                  canGoBack={!!previousItem}
                  onSave={handleSave}
                  onSkip={() => nextTodo && navigate(getGoldSetReviewUrl(nextTodo.short_key, pass))}
                  onPrevious={() =>
                    previousItem && navigate(getGoldSetReviewUrl(previousItem.short_key, pass))
                  }
                />
              )}
              <ReviewRulesPanel />
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  )
}
